import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ScheduledArchivationAlert } from '@/components/scheduled-archivation-alert';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form } from '@/components/ui/form';
import { displayToastError } from '@/helpers/display-toast';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateProjectQueries } from '@/modules/common/utils/query-invalidation';
import { archiveProject, getAvailableArchivationDateRange } from '@/modules/projects';
import { Project } from '@/modules/projects/types/project';
import { formatDisplayDate, getOptimalPresetDate } from '@/utils/date-formatters';

interface ArchiveProjectModalProps {
  project: Project;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const archiveSchema = z.object({
  archiveDate: z.date({
    required_error: 'authenticated.projects:archive.archiveDateRequired',
  }),
});

type ArchiveFormData = z.infer<typeof archiveSchema>;

/**
 * A comprehensive project archiving modal with date validation and form integration.
 * Provides controlled archiving workflow with proper date constraints and user feedback.
 *
 * **Key Features:**
 * - **Smart Date Validation**: Prevents archiving before last active assignment date
 * - **Form Integration**: React Hook Form with Zod validation and real-time feedback
 * - **Dual State Management**: Supports both internal and external modal state control
 * - **Comprehensive UX**: Clear messaging about archiving consequences and process
 * - **API Integration**: Handles async archiving operations with proper error states
 * - **Data Consistency**: Invalidates relevant query caches after successful archiving
 * - **Accessibility**: Full keyboard navigation and screen reader support
 *
 * **Validation Logic:**
 * - Fetches last active assignment date when modal opens
 * - Disables all dates before and including the last active assignment date
 * - Requires archive date selection before enabling confirmation
 * - Provides real-time form validation feedback
 *
 * **Archive Process:**
 * 1. User selects valid archive date (after last assignment)
 * 2. Modal explains what happens during archiving
 * 3. Confirmation triggers API call with selected date
 * 4. Success invalidates project caches and closes modal
 * 5. Error keeps modal open for retry
 *
 * @example
 * ```tsx
 * // Basic usage with internal state management
 * <ArchiveProjectModal
 *   project={selectedProject}
 *   onSuccess={() => {
 *     toast.success('Project archived successfully');
 *     navigate('/projects');
 *   }}
 * />
 *
 * // With custom trigger button
 * <ArchiveProjectModal
 *   project={project}
 *   trigger={
 *     <Button variant="outline" size="sm">
 *       <ArchiveIcon className="w-4 h-4 mr-2" />
 *       Archive This Project
 *     </Button>
 *   }
 *   onSuccess={() => refetchProjects()}
 * />
 *
 * // External state management (for complex flows)
 * function ProjectManagement() {
 *   const [showArchiveModal, setShowArchiveModal] = useState(false);
 *   const [selectedProject, setSelectedProject] = useState<Project | null>(null);
 *
 *   const handleArchiveClick = (project: Project) => {
 *     setSelectedProject(project);
 *     setShowArchiveModal(true);
 *   };
 *
 *   return (
 *     <>
 *       <ProjectList onArchive={handleArchiveClick} />
 *
 *       {selectedProject && (
 *         <ArchiveProjectModal
 *           project={selectedProject}
 *           open={showArchiveModal}
 *           onOpen={() => setShowArchiveModal(true)}
 *           onClose={() => {
 *             setShowArchiveModal(false);
 *             setSelectedProject(null);
 *           }}
 *           onSuccess={() => {
 *             setShowArchiveModal(false);
 *             setSelectedProject(null);
 *             queryClient.invalidateQueries(['projects']);
 *           }}
 *         />
 *       )}
 *     </>
 *   );
 * }
 *
 * // In dropdown menu context
 * <DropdownMenu>
 *   <DropdownMenuContent>
 *     <DropdownMenuItem asChild>
 *       <ArchiveProjectModal
 *         project={project}
 *         trigger={
 *           <span className="flex items-center">
 *             <ArchiveIcon className="w-4 h-4 mr-2" />
 *             Archive Project
 *           </span>
 *         }
 *         onSuccess={() => {
 *           toast.success('Project archived');
 *           closeDropdown();
 *         }}
 *       />
 *     </DropdownMenuItem>
 *   </DropdownMenuContent>
 * </DropdownMenu>
 *
 * // Bulk operations with conditional archiving
 * function BulkArchiveProjects() {
 *   const [selectedProjects, setSelectedProjects] = useState<Project[]>([]);
 *   const [currentArchiveProject, setCurrentArchiveProject] = useState<Project | null>(null);
 *
 *   const handleBulkArchive = async () => {
 *     for (const project of selectedProjects) {
 *       setCurrentArchiveProject(project);
 *       // Modal will handle each project individually with proper date validation
 *     }
 *   };
 *
 *   return currentArchiveProject ? (
 *     <ArchiveProjectModal
 *       project={currentArchiveProject}
 *       open={true}
 *       onClose={() => setCurrentArchiveProject(null)}
 *       onSuccess={() => {
 *         // Move to next project or complete bulk operation
 *         const remaining = selectedProjects.filter(p => p.id !== currentArchiveProject.id);
 *         setSelectedProjects(remaining);
 *         setCurrentArchiveProject(remaining[0] || null);
 *       }}
 *     />
 *   ) : null;
 * }
 *
 * // With error handling and retry logic
 * <ArchiveProjectModal
 *   project={project}
 *   onSuccess={() => {
 *     // Success handling
 *     analytics.track('project_archived', { projectId: project.id });
 *     toast.success('Project archived successfully');
 *   }}
 *   onError={(error) => {
 *     // Error is handled internally, but you can add additional logic
 *     console.error('Archive failed:', error);
 *     analytics.track('archive_failed', { projectId: project.id, error: error.message });
 *   }}
 * />
 * ```
 */
export function ArchiveProjectModal({
  project,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: ArchiveProjectModalProps) {
  const { t } = useTranslation(['authenticated.projects', 'common']);
  const queryClient = useQueryClient();
  const { getErrorMessage } = useApiError();

  const form = useForm<ArchiveFormData>({
    resolver: zodResolver(archiveSchema),
    mode: 'onChange', // Enable real-time validation
    defaultValues: {
      archiveDate: undefined,
    },
  });

  const archiveProjectMutation = useMutation({
    mutationFn: ({ projectId, archiveDate }: { projectId: string; archiveDate?: Date }) =>
      archiveProject({ projectId, archiveDate }),
    onSuccess: async () => {
      await invalidateProjectQueries(queryClient);
      form.reset();
      onSuccess?.();
    },
    onError: (error: unknown) => {
      displayToastError(getErrorMessage(error, true));
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine the actual modal open state (external or internal)
  const actualModalOpen = open !== undefined ? open : isModalOpen;

  // Fetch available archivation date range for date validation
  const {
    data: availableArchivationDateRange,
    isLoading: isLoadingAvailableDates,
    error: availableDatesError,
  } = useQuery({
    queryKey: ['available-archivation-date', project.id],
    queryFn: () => getAvailableArchivationDateRange(project.id.toString()),
    enabled: actualModalOpen,
  });

  // Extract start and end dates from the range
  const startDate = availableArchivationDateRange?.startDate;
  const endDate = availableArchivationDateRange?.endDate;

  // Pre-set the optimal date once loaded
  useEffect(() => {
    if (!form.getValues('archiveDate') && startDate) {
      const dateToSet = getOptimalPresetDate(startDate, endDate);
      form.setValue('archiveDate', dateToSet, { shouldValidate: true });
    }
  }, [startDate, endDate, form]);

  const handleArchive = async (closeModal: () => void) => {
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    const formData = form.getValues();

    try {
      await archiveProjectMutation.mutateAsync({
        projectId: project.id.toString(),
        archiveDate: formData.archiveDate,
      });
      closeModal();
    } catch {
      // Error handled by mutation onError, keep modal open for retry
    }
  };

  // Function to disable dates outside the available archivation range
  const isDateDisabled = (date: Date): boolean => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Disable dates before the start date
    if (startDate) {
      const startDateOnly = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
      );
      if (dateOnly < startDateOnly) {
        return true;
      }
    }

    // Disable dates after the end date
    if (endDate) {
      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      if (dateOnly > endDateOnly) {
        return true;
      }
    }

    return false;
  };

  const archiveDate = form.watch('archiveDate');
  const isFormValid = !!archiveDate;

  const messageContent = (
    <div className="space-y-4">
      {availableDatesError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-5 w-5" />
          <AlertDescription>
            {t('authenticated.projects:archive.unableToLoadArchivationDate')}
          </AlertDescription>
        </Alert>
      )}
      {project.nextArchiveDate && (
        <ScheduledArchivationAlert
          message={t('authenticated.projects:scheduledArchive.willBeArchivedOn', {
            date: formatDisplayDate(project.nextArchiveDate),
          })}
          className="mb-0"
        />
      )}
      <div>
        <Trans
          i18nKey="authenticated.projects:archive.archiveProjectMessage"
          components={{ strong: <strong /> }}
        />
      </div>
      <div className="space-y-2">
        <p className="font-semibold">{t('authenticated.projects:archive.whatHappens')}</p>
        <ul className="ml-2 list-inside list-disc space-y-1">
          <li>{t('authenticated.projects:archive.projectWillBeHidden')}</li>
          <li>{t('authenticated.projects:archive.dataWillBePreserved')}</li>
          <li>{t('authenticated.projects:archive.canBeRestored')}</li>
          <li>{t('authenticated.projects:archive.noTracking')}</li>
        </ul>
      </div>
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.projects:archive.archiveProjectTitle')}
      message={messageContent}
      confirmText={
        archiveProjectMutation.isPending
          ? t('authenticated.projects:archive.archiving')
          : t('authenticated.projects:archive.archiveProject')
      }
      confirmVariant="default"
      onConfirm={handleArchive}
      isLoading={archiveProjectMutation.isPending}
      disabled={!isFormValid || isLoadingAvailableDates || !!availableDatesError}
      open={actualModalOpen}
      onOpen={onOpen || (() => setIsModalOpen(true))}
      onClose={
        onClose ||
        (() => {
          setIsModalOpen(false);
          form.reset();
        })
      }
    >
      <Form {...form}>
        <form className="space-y-4">
          <DatePicker
            name="archiveDate"
            control={form.control}
            label={t('authenticated.projects:archive.archiveDate')}
            dateFormat="D MMM, YYYY"
            disabled={isDateDisabled}
            inputDisabled={isLoadingAvailableDates}
            isLoading={isLoadingAvailableDates}
          />
        </form>
      </Form>
    </ConfirmationModal>
  );
}
